import { Component, ChangeDetectionStrategy, ViewEncapsulation, Input, ViewChild, TemplateRef, ViewContainerRef, AfterViewInit, OnDestroy,
    Output, EventEmitter, Directive, ContentChild, forwardRef, QueryList, ElementRef } from '@angular/core';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

import { EuiMessageBoxService } from './services/eui-message-box.service';
import { EuiMessageBoxInterface } from './models/eui-message-box.config';

@Component({
    selector: 'eui-message-box',
    templateUrl: './eui-message-box.component.html',
    styleUrls: ['./styles/_index.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})

export class EuiMessageBoxComponent implements AfterViewInit, OnDestroy {

    @Input() e2eAttr: string = 'eui-message-box';
    @Input() title: string;
    @Input() acceptLabel: string = 'eui.YES';
    @Input() dismissLabel: string = 'eui.NO';
    @Input() type: ('info' | 'success' | 'warning' | 'danger' | 'question') = 'warning';
    @Input()
    get hasCloseButton() { return this._hasCloseButton; }
    set hasCloseButton(value) { this._hasCloseButton = coerceBooleanProperty(value); }
    _hasCloseButton = true;
    @Input()
    get closeOnClickOutside() { return this._closeOnClickOutside; }
    set closeOnClickOutside(value) { this._closeOnClickOutside = coerceBooleanProperty(value); }
    _closeOnClickOutside = false;

    @Output() outsideClick = new EventEmitter();
    @Output() open = new EventEmitter();
    @Output() close = new EventEmitter();
    @Output() dismiss = new EventEmitter();
    @Output() accept = new EventEmitter();

    public content: string | TemplatePortal;

    @ViewChild('templateRefContent') templateRefContent: TemplateRef<ElementRef>;
    @ViewChild('templateRefFooter') templateRefFooter: TemplateRef<EuiMessageBoxFooterDirective>;

    @ContentChild(forwardRef(() => EuiMessageBoxFooterDirective)) euiMessageBoxFooterDirective: QueryList<EuiMessageBoxFooterDirective>;

    private templatePortalContent: TemplatePortal<ElementRef>;
    private templatePortalFooter: TemplatePortal<EuiMessageBoxFooterDirective>;
    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(private viewContainerRef: ViewContainerRef, private euiMessageBoxService: EuiMessageBoxService) {
    }

    ngAfterViewInit(): void {
        this.templatePortalContent = new TemplatePortal(this.templateRefContent, this.viewContainerRef);

        if (this.euiMessageBoxFooterDirective) {
            this.templatePortalFooter = new TemplatePortal(this.templateRefFooter, this.viewContainerRef);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    /**
     * Open a Message Box
     */
    public openMessageBox(): void {
        const config: EuiMessageBoxInterface = {
            e2eAttr: this.e2eAttr,
            title: this.title,
            type: this.type,
            acceptLabel: this.acceptLabel,
            dismissLabel: this.dismissLabel,
            hasCloseButton: this.hasCloseButton,
            closeOnClickOutside: this.closeOnClickOutside,
            content: this.templatePortalContent,
            footer: this.templatePortalFooter,
            outsideClick: () => {
                this.outsideClick.emit();
            },
            open: () => {
                this.open.emit();
            },
            close: () => {
                this.close.emit();
            },
            dismiss: () => {
                this.dismiss.emit();
            },
            accept: () => {
                this.accept.emit();
            },
        };

        this.euiMessageBoxService.openMessageBox(config);
    }

    /**
     * Close a Message Box
     */
    public closeMessageBox(): void {
        this.euiMessageBoxService.closeMessageBox();
    }
}

/* tslint:disable */
@Directive({ selector: 'eui-message-box-footer' })
export class EuiMessageBoxFooterDirective { }
/* tslint:enable */
